import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogInsights = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog?limit=3`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      // Use fallback data if API fails
      setPosts([
        {
          id: '1',
          title: 'The Rise of Second Homes in Maharashtra Hill Stations',
          excerpt:
            'Exploring the growing trend of luxury second homes in Lonavala and Khandala as HNIs seek weekend sanctuaries away from urban chaos.',
          category: 'Market Insights',
          author: 'Venara Estate',
          created_at: new Date().toISOString(),
          image_url: 'https://images.unsplash.com/photo-1644620426342-471c3ed4e1d4?crop=entropy&cs=srgb&fm=jpg&q=85',
        },
        {
          id: '2',
          title: 'Dubai Real Estate: A Guide for Indian Investors',
          excerpt:
            'Understanding investment opportunities, legal frameworks, and tax implications for Indian HNWIs investing in Dubai luxury properties.',
          category: 'International Markets',
          author: 'Venara Estate',
          created_at: new Date().toISOString(),
          image_url: 'https://images.unsplash.com/photo-1748373448914-1d7f882700e2?crop=entropy&cs=srgb&fm=jpg&q=85',
        },
        {
          id: '3',
          title: 'Alibaug: The New Riviera for Mumbai Elite',
          excerpt:
            'How Alibaug transformed from a weekend getaway to a premier destination for luxury villas and beachfront estates.',
          category: 'Location Spotlight',
          author: 'Venara Estate',
          created_at: new Date().toISOString(),
          image_url: 'https://images.unsplash.com/photo-1728486888025-34e8d66b6b7b?crop=entropy&cs=srgb&fm=jpg&q=85',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMMM dd, yyyy');
    } catch {
      return 'Recent';
    }
  };

  if (loading) {
    return (
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-background">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-muted-foreground">Loading insights...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-background" data-testid="blog-insights-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-secondary text-sm font-medium tracking-widest uppercase">Market Intelligence</span>
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-primary mt-4 mb-6 tracking-tight">
            Latest Insights
          </h2>
          <p className="text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Stay informed with expert perspectives on luxury real estate trends, market analysis, and investment
            opportunities.
          </p>
        </motion.div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`blog-post-${index}`}
              className="group bg-white border border-muted hover:border-secondary transition-all duration-500 overflow-hidden hover:shadow-[0_20px_40px_-4px_rgba(15,40,30,0.2)]"
            >
              {/* Image */}
              <div className="relative overflow-hidden h-64">
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-secondary text-secondary-foreground px-3 py-1 text-xs font-medium uppercase">
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs mb-3">
                  <Calendar size={14} />
                  <span>{formatDate(post.created_at)}</span>
                </div>

                <h3 className="font-playfair text-xl font-semibold text-primary mb-3 group-hover:text-secondary transition-colors duration-300">
                  {post.title}
                </h3>

                <p className="text-muted-foreground text-sm leading-relaxed mb-4">{post.excerpt}</p>

                <div className="flex items-center gap-2 text-secondary text-sm font-medium group-hover:gap-4 transition-all duration-300">
                  Read More
                  <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform duration-300" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* View All CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-12"
        >
          <a
            href="/insights"
            data-testid="view-all-insights-cta"
            className="inline-block bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-6 font-manrope tracking-wide uppercase text-xs transition-all duration-300"
          >
            View All Insights
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogInsights;
